/**
 * Mesa Vision C T10 — i18n del estudio. Follows the app language (useLanguage / Nav toggle);
 * a component's `lang` prop still overrides it. Keys live in STUDY_STRINGS and are also
 * registered in the global translations, so `useLanguage().t("study.…")` works too.
 */

import { useCallback } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import { STUDY_STRINGS } from "@/lib/studyI18nStrings";

export type StudyLang = "es" | "en";
export type StudyT = (key: string, vars?: Record<string, string | number>) => string;

/** Pure lookup + `{a}` interpolation; unknown keys come back as the key (visible, never blank). */
export function studyT(lang: StudyLang): StudyT {
  return (key, vars) => {
    const raw = STUDY_STRINGS[key]?.[lang] ?? key;
    return vars ? raw.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m)) : raw;
  };
}

export function useStudyI18n(override?: StudyLang): { t: StudyT; lang: StudyLang; es: boolean } {
  const ctx = useLanguage();
  const lang: StudyLang = override ?? ctx.lang;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const t = useCallback(studyT(lang), [lang]);
  return { t, lang, es: lang !== "en" };
}
